import { inject } from '@angular/core';
import { PlusAuthenticationService } from '@karve.it/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ActionType, Store } from '@ngrx/store';
import { MutationOptionsAlone } from 'apollo-angular/types';
import {
    CommentMutatedGQL,
  CreateCommentGQL,
  CreateCommentMutation,
  CreateCommentMutationVariables,
  DeleteCommentsGQL,
  DeleteCommentsMutationVariables,
  UpdateCommentsGQL,
  UpdateCommentsMutationVariables,
  UpdateJobGQL,
  UpdateJobMutationVariables,
} from 'graphql.generated';
import { ConfirmationService } from 'primeng/api';
import { Delta } from 'quill';
import {
  catchError,
  exhaustMap,
  filter,
  from,
  map,
  mergeMap,
  of,
  tap,
  distinctUntilChanged,
  switchMap,
  EMPTY,
} from 'rxjs';

import { generateMutationContext } from 'src/app/core/auth/session';
import { ArtifactService } from 'src/app/core/public-api';
import { DatadogService } from 'src/app/services/datadog.service';
import { FreyaHelperService } from 'src/app/services/freya-helper.service';
import { FreyaNotificationsService } from 'src/app/services/freya-notifications.service';
import { brandingFeature } from 'src/app/state/branding.store';

import { MS_ONE_SECOND, currentTimeSeconds } from 'src/app/time';
import { LoadingState, generateUUID } from 'src/app/utilities/state.util';

import { selectJobTemplateVariableData } from '../../shared/quill-modules/quill-template-variable-data.registry';
import { convertQuillContentsToText, replaceQuillVariablesWithActualValues } from '../../shared/quill-modules/quill-templates.helpers';
import { selectJobPageChanges } from '../../shared/unsaved-changes-banner/unsaved-changes-banner.selectors';
import { JobCreateCustomerActions } from '../job-create/job-create-customer/job-create-customer.actions';
import { JobToolSubscriptionActions } from '../job-state/job-tool-subscription.actions';
import { jobToolFeature, selectJobZone, selectJobId, selectJobFranchiseId } from '../job-tool.reducer';


import { COMMENT_ATTACHMENT_MAX_FILE_SIZE, COMMENT_ATTACHMENT_MAX_TOTAL_SIZE, isDeltaBlank, validatePendingFiles } from './comments.utils';
import { JobActivityActions } from './job-activity.actions';
import { findComment, getCommentSubject } from './job-activity.reducer';
import { EditableComment } from './job-activity.state';
import { JobCommentArtifactHandlerService } from './job-comment-artifact-handler.service';

/**
 * Replaces template variable embeds with their current values. The editor only
 * fills variable values in silently, so the delta in the store still holds the
 * template's empty values; the backend sends comment contents as-is (email,
 * SMS text), so they must be resolved before they leave the client.
 */
function resolveTemplateVariables(contents: Delta | undefined, variableData: unknown): Delta | undefined {
  if (!contents?.ops) return contents;
  return replaceQuillVariablesWithActualValues(contents, variableData);
}

/**
 * subjectContents is usually Delta JSON, but the schema accepts any string, so
 * plain text is wrapped in a Delta rather than dropped (same as comment subjects).
 * A template without a subject yields an empty Delta, so selecting it clears the
 * previous template's subject instead of leaving it in place.
 */
function parseTemplateSubject(subjectContents: string | null | undefined): Delta {
  return new Delta(getCommentSubject(subjectContents));
}

export const addComment = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  auth = inject(PlusAuthenticationService),
  datadog = inject(DatadogService),
) => {
    return actions$.pipe(
      ofType(
        JobActivityActions.addCommentButtonClicked,
        JobActivityActions.addCommentCtrlEnterPressed,
      ),
      concatLatestFrom(() => [
        store.select(jobToolFeature.selectAddComment),
        store.select(selectJobTemplateVariableData),
      ]),
      tap(([ _action, { contents, commentModeId, pendingFiles }]) => {
        const isBlank = isDeltaBlank(contents);
        const deltaOpsCount = contents?.ops?.length || 0;
        const deltaBytes = new TextEncoder().encode(JSON.stringify(contents)).byteLength;

        datadog.addAction('comment_submit_clicked', {
          is_blank: isBlank,
          delta_ops_count: deltaOpsCount,
          delta_bytes: deltaBytes,
          pending_file_count: pendingFiles.length,
          comment_mode_id: commentModeId,
        });

        if (isBlank && pendingFiles.length === 0) {
          datadog.addAction('comment_submit_filtered', {
            reason: 'blank_contents',
            delta_ops_count: deltaOpsCount,
            delta_bytes: deltaBytes,
          });
        }
      }),
      filter(([ _action, { contents, pendingFiles }]) => !isDeltaBlank(contents) ||  pendingFiles.length > 0),
      map(([ _action, { subject, contents, commentModeId, pendingFiles }, variableData ]) => ({
        comment: {
          id: generateUUID(),
          contents: resolveTemplateVariables(contents, variableData),
          subject: resolveTemplateVariables(subject, variableData),
          author: auth.user,
          threadId: generateUUID(),
          replies: [],
          createdAt: currentTimeSeconds() * MS_ONE_SECOND,
          callState: LoadingState.MUTATING,
          modeId: commentModeId,
          artifacts: pendingFiles.map((f) => ({
            id: f.id,
            name: f.name,
            contentType: f.type,
            fileSize: f.size,
          })),
        } as EditableComment,
        pendingFileIds: pendingFiles.map((f) => f.id),
      })),
      tap(({ pendingFileIds }) => datadog.addAction('comment_optimistic_added', {
        pending_file_count: pendingFileIds.length,
      })),
      map(({ comment, pendingFileIds }) => JobActivityActions.newCommentAdded({ comment, pendingFileIds })),
    );
}, { functional: true, dispatch: true });

export const addReply = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  auth = inject(PlusAuthenticationService),
) => {
    return actions$.pipe(
      ofType(
        JobActivityActions.addReplyButtonClicked,
        JobActivityActions.replyInputEnterKeyPressed,
      ),
      concatLatestFrom(() => [
        store.select(jobToolFeature.selectComments),
        store.select(jobToolFeature.selectAddReplyContents),
        store.select(jobToolFeature.selectAddReplyPendingFiles),
        store.select(selectJobTemplateVariableData),
      ]),
      map(([ { commentId, parentId }, comments, addReplyContents, addReplyPendingFiles, variableData ]) => {

        const comment = findComment({
          comments,
          commentId,
          parentId,
        });

        const pendingFiles = addReplyPendingFiles[commentId] || [];

        const reply: EditableComment = {
          id: generateUUID(),
          parentId: parentId || commentId,
          contents: resolveTemplateVariables(addReplyContents[commentId], variableData),
          author: auth.user,
          threadId: comment.threadId,
          replies: [],
          createdAt: currentTimeSeconds() * MS_ONE_SECOND,
          callState: LoadingState.MUTATING,
          artifacts: pendingFiles.map((f) => ({
            id: f.id,
            name: f.name,
            contentType: f.type,
            fileSize: f.size,
          })),
        };

        return {
          commentId,
          parentId,
          comment: reply,
          pendingFileIds: pendingFiles.map((f) => f.id),
        };
      }),
      map((payload) => JobActivityActions.replyAdded(payload)),
    );
}, { functional: true, dispatch: true });

export const uploadCommentAttachments = createEffect((
  actions$ = inject(Actions),
  artifactHandler = inject(JobCommentArtifactHandlerService),
  artifactService = inject(ArtifactService),
) => {
  return actions$.pipe(
    ofType(JobActivityActions.newCommentAdded),
    map(({ comment, pendingFileIds }) => ({
      commentId: comment.id,
      pendingFileIds,
      files: (pendingFileIds || [])
        .map((id) => artifactHandler.getFile(id))
        .filter((file): file is File => !!file),
    })),
    filter(({ pendingFileIds }) => pendingFileIds?.length > 0),
    mergeMap(({ commentId, files, pendingFileIds }) =>
      of(null).pipe(
        tap(() => {
          const failCount = pendingFileIds.length - files.length;

          if (failCount > 0) {
            throw new Error(`Failed to resolve ${failCount} of ${pendingFileIds.length} pending file(s) for upload`);
          }
        }),
        mergeMap(() =>artifactService.uploadCommentArtifacts(files).pipe(
          map((artifacts) => JobActivityActions.addCommentAttachmentsUploaded({
            commentId,
            artifactIds: artifacts.map((a) => a.id),
            fileIds: pendingFileIds,
          })),
        )),
        catchError((error) =>
          of(JobActivityActions.addCommentAttachmentsUploadError({
            commentId,
            error,
          })),
        ),
      ),
    ),
  );
}, { functional: true, dispatch: true });

export const uploadReplyAttachments = createEffect((
  actions$ = inject(Actions),
  artifactHandler = inject(JobCommentArtifactHandlerService),
  artifactService = inject(ArtifactService),
) => {
  return actions$.pipe(
    ofType(JobActivityActions.replyAdded),
    map(({ comment, parentId, pendingFileIds }) => ({
      commentId: comment.id,
      parentId: parentId || comment.parentId,
      pendingFileIds,
      files: (pendingFileIds || [])
        .map((id) => artifactHandler.getFile(id))
        .filter((file): file is File => !!file),
    })),
    filter(({ pendingFileIds }) => pendingFileIds?.length > 0),
    mergeMap(({ commentId, parentId, files, pendingFileIds }) =>
      of(null).pipe(
        tap(() => {
          const failCount = pendingFileIds.length - files.length;

          if (failCount > 0) {
            throw new Error(`Failed to resolve ${failCount} of ${pendingFileIds.length} pending file(s) for upload`);
          }
        }),
        mergeMap(() => artifactService.uploadCommentArtifacts(files).pipe(
          map((artifacts) => JobActivityActions.replyAttachmentsUploaded({
            commentId,
            parentId,
            artifactIds: artifacts.map((a) => a.id),
            fileIds: pendingFileIds,
          })),
        )),
        catchError((error) =>
          of(JobActivityActions.replyAttachmentsUploadError({
            commentId,
            parentId,
            error,
          })),
        ),
      ),
    ),
  );
}, { functional: true, dispatch: true });

export const createComment = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  createCommentGQL = inject(CreateCommentGQL),
  datadog = inject(DatadogService),
) => {
    return actions$.pipe(
      ofType(
        JobActivityActions.newCommentAdded,
        JobActivityActions.replyAdded,
        JobActivityActions.addCommentAttachmentsUploaded,
        JobActivityActions.replyAttachmentsUploaded,
      ),
      filter((action) => {
        if (action.type === JobActivityActions.newCommentAdded.type) {
          return !action.pendingFileIds?.length;
        }
        if (action.type === JobActivityActions.replyAdded.type) {
          return !action.pendingFileIds?.length;
        }
        return true;
      }),
      concatLatestFrom(() => [
        store.select(jobToolFeature.selectAddComment),
        store.select(jobToolFeature.selectComments),
        store.select(selectJobId),
        store.select(selectJobZone),
        store.select(selectJobPageChanges),
      ]),
      map(([ action, { commentModeId }, comments, jobId, jobZone, changes ]): {
        vars: CreateCommentMutationVariables;
        opts: MutationOptionsAlone<CreateCommentMutation, CreateCommentMutationVariables>;
        jobId: string;
        changeIds: string[];
      } | null => {

        let newComment: EditableComment;
        let artifactIds: string[] | undefined;

        if (action.type === JobActivityActions.addCommentAttachmentsUploaded.type) {
          newComment = comments.find((c) => c.id === action.commentId);
          artifactIds = action.artifactIds;
        } else if (action.type === JobActivityActions.replyAttachmentsUploaded.type) {
          newComment = findComment({ comments, commentId: action.commentId, parentId: action.parentId });
          artifactIds = action.artifactIds;
        } else {
          newComment = action.comment;
        }

        if (!newComment) {
          console.error('Comment not found for creation:', action);
          return null;
        }

        const changeTypeIdentifier = action.type === JobActivityActions.replyAdded.type
          ? 'Reply Input'
          : 'Add Comment';

        const changeIds = changes
            .filter((c) => c.action.type.includes(changeTypeIdentifier))
            .map((c) => c.id);

        return {
          vars: {
            input: {
              id: newComment.id,
              threadId: newComment.threadId,
              contents: JSON.stringify(newComment.contents),
              subject: newComment.subject ? convertQuillContentsToText(newComment.subject) : undefined,
              text: '',
              objectId: jobId,
              objectType: 'Job',
              mode: newComment.modeId || commentModeId,
              addArtifacts: artifactIds,
            }
          },
          opts: {
            context: generateMutationContext({
              zone: jobZone.id,
              actionId: newComment.id,
            }),
          },
          changeIds,
          jobId,
        }
      }),
      filter((result): result is NonNullable<typeof result> => result !== null),
      exhaustMap(({ vars, opts, jobId, changeIds }) => {
        datadog.addAction('comment_mutation_started', {
          unsaved_change_count: changeIds.length,
          artifact_count: vars.input.addArtifacts?.length || 0,
        });

        return createCommentGQL.mutate(vars, opts).pipe(
          map((res) => JobActivityActions.createCommentSuccess({
            jobId,
            newComment: res.data.addComment,
            changeIds,
          })),
          tap(() => datadog.addAction('comment_mutation_succeeded')),
          catchError((error) => {
            datadog.addAction('comment_mutation_failed', {
              error_name: error instanceof Error ? error.name : 'UnknownError',
            });
            return of(JobActivityActions.createCommentError({ error }));
          }),
        );
      }),
    );
}, { functional: true, dispatch: true });

export const updateComment = createEffect((
  actions$ = inject(Actions),
  updateCommentGQL = inject(UpdateCommentsGQL),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(
        JobActivityActions.saveCommentEditsButtonClicked,
        JobActivityActions.commentEditEnterKeyPressed,
      ),
      concatLatestFrom(() => [
        store.select(jobToolFeature.selectComments),
        store.select(selectJobId),
        store.select(selectJobTemplateVariableData),
      ]),
      map(([ { commentId, parentId }, comments, jobId, variableData ]): {
        vars: UpdateCommentsMutationVariables;
        jobId: string;
        commentId: string;
        parentId: string;
      } => {

        const comment = findComment({
          comments,
          commentId,
          parentId,
        });

        const input = {
          ids: [ commentId ],
          update: {
            contents: JSON.stringify(resolveTemplateVariables(comment.contents, variableData)),
          },
        }

        return {
          vars: { input },
          jobId,
          commentId,
          parentId,
        };
      }),
      exhaustMap(({ vars, jobId, commentId, parentId }) => updateCommentGQL.mutate(vars).pipe(
        map((res) => JobActivityActions.updateCommentSuccess({
          comment: res.data.updateComments[0],
          jobId,
          commentId,
          parentId,
        })),
        catchError((error) => of(JobActivityActions.updateCommentError({ commentId, parentId, error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const confirmDeleteComment = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  confirmationService = inject(ConfirmationService),
) => {
  return actions$.pipe(
    ofType(
      JobActivityActions.deleteCommentMenuItemClicked,
    ),
    tap(({ commentId, parentId }) => confirmationService.confirm({
      header: 'Are you sure you want to delete this comment?',
      message: 'This action cannot be undone.',
      rejectButtonStyleClass: 'p-button-outlined',
      acceptButtonStyleClass: 'p-button-danger',
      rejectLabel: 'Cancel',
      acceptLabel: 'Delete Comment',
      accept: () => store.dispatch(JobActivityActions.confirmDeleteCommentButtonClicked({ commentId, parentId })),
    })),
  );
}, { functional: true, dispatch: false });

export const deleteComment = createEffect((
  actions$ = inject(Actions),
  deleteCommentGQL = inject(DeleteCommentsGQL),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(
        JobActivityActions.deleteCommentButtonClicked,
        JobActivityActions.confirmDeleteCommentButtonClicked,
      ),
      concatLatestFrom(() => store.select(selectJobId)),
      map(([ { commentId, parentId }, jobId ]): {
        vars: DeleteCommentsMutationVariables;
        commentId: string;
        parentId?: string;
        jobId: string;
      } => ({
        vars: { input: { ids: [ commentId ] } },
        commentId,
        parentId,
        jobId,
      })),
      exhaustMap(({ vars, commentId, parentId, jobId }) => deleteCommentGQL.mutate(vars).pipe(
        map(() => JobActivityActions.deleteCommentSuccess({ commentId, parentId, jobId })),
        catchError((error) => of(JobActivityActions.deleteCommentError({ commentId, parentId, error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const copyTimeToClickboard = createEffect((
  actions$ = inject(Actions),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobActivityActions.quillEditorTimeClicked),
      tap(({ time }) => {
        navigator.clipboard.writeText(time).then(() => {
          notify.info('Copied to clipboard', time);
        }).catch((err) => {
          console.error('Clipboard copy failed:', err);
        });
      })
    );
}, { functional: true, dispatch: false });

export const setCommentMode = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(
        JobActivityActions.addCommentModeSelected,
        JobCreateCustomerActions.sendMessageToCustomer,
      ),
      concatLatestFrom(() => store.select(brandingFeature.selectCommentModes)),
      map(([ { commentModeId }, commentModes ]) => commentModes.find((m) => m.id === commentModeId)),
      map((commentMode) => JobActivityActions.addCommentModeSet(commentMode)),
    );
}, { functional: true, dispatch: true });

/**
 * The template toolbar only fills the editor it belongs to (the body), so copy
 * the template's subject into the subject editor. Variables are resolved to
 * plain text since the subject is sent as text anyway.
 */
export const setTemplateSubject = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobActivityActions.addCommentTemplateSelected),
      map(({ template }) => parseTemplateSubject(template?.subjectContents)),
      concatLatestFrom(() => [
        store.select(jobToolFeature.selectAddComment),
        store.select(brandingFeature.selectCommentModes),
        store.select(selectJobTemplateVariableData),
      ]),
      // The fallback default mode has no subject field, so only configured modes matter
      filter(([ _subject, { commentModeId }, commentModes ]) =>
        Boolean(commentModes?.find((m) => m.id === commentModeId)?.showSubject)),
      map(([ subject, _addComment, _commentModes, variableData ]) => JobActivityActions.addCommentTemplateSubjectSet({
        subject: replaceQuillVariablesWithActualValues(subject, variableData),
      })),
    );
}, { functional: true, dispatch: true });

export const validateAndAttachFiles = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobActivityActions.addCommentFilesSelected),
      concatLatestFrom(() => store.select(jobToolFeature.selectAddComment)),
      map(([{ files }, { pendingFiles: existing }]) => validatePendingFiles(files, existing)),
      mergeMap(({ valid, discarded }) => {
        const result = [];
        if (valid.length) {
          result.push(JobActivityActions.addCommentFilesAttached({ files: valid }));
        }
        if (discarded.length) {
          result.push(JobActivityActions.addCommentFilesDiscarded({ files: discarded }));
        }
        return from(result);
      }),
    );
}, { functional: true, dispatch: true });

export const validateAndAttachReplyFiles = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobActivityActions.replyFilesSelected),
      concatLatestFrom(() => store.select(jobToolFeature.selectAddReplyPendingFiles)),
      map(([{ commentId, files }, addReplyPendingFiles]) => ({
        commentId,
        ...validatePendingFiles(files, addReplyPendingFiles[commentId] || []),
      })),
      mergeMap(({ commentId, valid, discarded }) => {
        const result = [];
        if (valid.length) {
          result.push(JobActivityActions.replyFilesAttached({ commentId, files: valid }));
        }
        if (discarded.length) {
          result.push(JobActivityActions.replyFilesDiscarded({ commentId, files: discarded }));
        }
        return from(result);
      }),
    );
}, { functional: true, dispatch: true });

export const notifyRejectedFiles = createEffect((
  actions$ = inject(Actions),
  notify = inject(FreyaNotificationsService),
) => {
    const maxMB = COMMENT_ATTACHMENT_MAX_FILE_SIZE / (1024 * 1024);

    return actions$.pipe(
      ofType(JobActivityActions.addCommentFilesRejected, JobActivityActions.replyFilesRejected),
      tap(({ rejected }) => {
        const byType = rejected.filter((r) => r.reason === 'type');
        if (byType.length) {
          notify.addToast.next({
            severity: 'error',
            summary: 'File type not allowed',
            detail: byType.map((r) => r.name).join(', '),
          });
        }

        const bySize = rejected.filter((r) => r.reason === 'size');
        if (bySize.length) {
          notify.addToast.next({
            severity: 'error',
            summary: `Files exceed ${maxMB} MB limit`,
            detail: bySize.map((r) => r.name).join(', '),
          });
        }
      }),
    );
}, { functional: true, dispatch: false });

export const notifyDiscardedFiles = createEffect((
  actions$ = inject(Actions),
  notify = inject(FreyaNotificationsService),
) => {
    const maxTotalMB = COMMENT_ATTACHMENT_MAX_TOTAL_SIZE / (1024 * 1024);

    return actions$.pipe(
      ofType(JobActivityActions.addCommentFilesDiscarded, JobActivityActions.replyFilesDiscarded),
      tap(({ files }) => {
        const duplicates = files.filter((f) => f.reason === 'duplicate');
        if (duplicates.length) {
          notify.addToast.next({
            severity: 'warn',
            summary: 'File already attached',
            detail: duplicates.map((f) => f.name).join(', '),
          });
        }

        const overBudget = files.filter((f) => f.reason === 'total-size');
        if (overBudget.length) {
          notify.addToast.next({
            severity: 'error',
            summary: `Total attachments cannot exceed ${maxTotalMB} MB`,
            detail: overBudget.map((f) => f.name).join(', '),
          });
        }
      }),
    );
}, { functional: true, dispatch: false });

export const subscribeToCommentMutatedEvents = createEffect((
  store = inject(Store),
  commentMutatedGQL = inject(CommentMutatedGQL),
) => {
    return store.select(selectJobId).pipe(
      distinctUntilChanged(),
      switchMap((jobId) => !jobId ? EMPTY : commentMutatedGQL.subscribe({ jobId }).pipe(
        map((val) => val.data.commentMutated),
        map(({ mutationType, comment }) => {
          if (mutationType === 'created') {
            return JobToolSubscriptionActions.commentCreated({ jobId, comment });
          } else if (mutationType === 'updated') {
            return JobToolSubscriptionActions.commentUpdated({ jobId, comment });
          } else {
            return JobToolSubscriptionActions.commentDeleted({ jobId, comment });
          }
        }),
      )),
    );

  }, { functional: true, dispatch: true });

export const openAttachment = createEffect((
  actions$ = inject(Actions),
  freyaHelper = inject(FreyaHelperService),
) => {
    return actions$.pipe(
      ofType(
        JobActivityActions.fileChipClicked,
      ),
      filter(({ artifact }) => Boolean(artifact)),
      tap((action: ActionType<typeof JobActivityActions.fileChipClicked>) => freyaHelper.openInDialog(action.artifact)),
    );
  }, { functional: true, dispatch: false });

export const updateHeartbeat = createEffect((
  actions$ = inject(Actions),
  updateJobGQL = inject(UpdateJobGQL),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobActivityActions.updateHeartbeatRequested),
      concatLatestFrom(() => store.select(selectJobFranchiseId)),
      map(([ { jobId, nextHeartbeat, aiDisabled }, jobFranchiseId ]): {
        vars: UpdateJobMutationVariables;
        opts: MutationOptionsAlone<any, any>;
      } => ({
        vars: {
            updateJobs: [{
                jobId,
                ...(nextHeartbeat !== undefined && { nextHeartbeat }),
                ...(aiDisabled !== undefined && { aiDisabled }),
            }]
        },
        opts: {
            context: generateMutationContext({
                zone: jobFranchiseId,
            }),
        }
      })),
      exhaustMap(({ vars, opts }) => updateJobGQL.mutate(vars, {
            ...opts,
            refetchQueries: ['JobsV3Page'],
        }).pipe(
            map(() => JobActivityActions.updateHeartbeatSuccess()),
            catchError((error) => of(JobActivityActions.updateHeartbeatError({ error: { message: error?.message || String(error) } }))),
        )),
    );
}, { functional: true, dispatch: true });
